import { bindMouseChordPanFix } from '@utils/camera/bindMouseChordPanFix'
import { useThree } from '@react-three/fiber'
import type CameraControls from 'camera-controls'
import { type RefObject, useEffect } from 'react'

type Options = {
  enabled?: boolean
}

export function useMouseChordPanCamera(
  controlsRef: RefObject<CameraControls | null>,
  options: Options = {},
) {
  const { gl } = useThree()
  const enabled = options.enabled ?? true

  useEffect(() => {
    if (!enabled) return

    let disposed = false
    let cleanup: (() => void) | undefined
    let frame = 0

    const bind = () => {
      if (disposed) return
      const controls = controlsRef.current
      if (!controls) {
        frame = requestAnimationFrame(bind)
        return
      }
      cleanup = bindMouseChordPanFix(controls, gl.domElement)
    }

    bind()

    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      cleanup?.()
    }
  }, [controlsRef, enabled, gl.domElement])
}

export function useMouseChordPanCameraFromControls(
  controls: CameraControls | null | undefined,
  options: Options = {},
) {
  const { gl } = useThree()
  const enabled = options.enabled ?? true

  useEffect(() => {
    if (!enabled || !controls) return
    return bindMouseChordPanFix(controls, gl.domElement)
  }, [controls, enabled, gl.domElement])
}
